"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import type { DiveLogEntry } from "@/lib/types";

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

type Props = {
  creatorSlug: string;
  displayName: string;
  /** Only the linked captain should add/remove dives; public visitors are read-only. */
  canManageDives: boolean;
  /** When false and `canManageDives` is false, show a sign-in affordance (captain may sign in). */
  viewerSignedIn?: boolean;
};

export function DiveLogManager({
  creatorSlug,
  displayName,
  canManageDives,
  viewerSignedIn = false,
}: Props) {
  const [logs, setLogs] = useState<DiveLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch(
        `/api/dive-logs?creatorSlug=${encodeURIComponent(creatorSlug)}`,
        { cache: "no-store" },
      );
      if (!res.ok) {
        setLoadError("Could not load dive log.");
        setLogs([]);
        return;
      }
      const data = (await res.json()) as DiveLogEntry[];
      setLogs(Array.isArray(data) ? data : []);
    } catch {
      setLoadError("Could not load dive log.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [creatorSlug]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (logs.length === 0) setShowHistory(false);
  }, [logs.length]);

  const [form, setForm] = useState({
    diveSite: "",
    date: "",
    maxDepthM: "",
    bottomTimeMin: "",
    waterTempC: "",
    visibilityM: "",
    gasMix: "",
    notes: "",
  });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const resetForm = useCallback(() => {
    setForm({
      diveSite: "",
      date: "",
      maxDepthM: "",
      bottomTimeMin: "",
      waterTempC: "",
      visibilityM: "",
      gasMix: "",
      notes: "",
    });
    setPendingFiles([]);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const site = form.diveSite.trim();
    const date = form.date.trim();
    const depth = Number(form.maxDepthM);
    const time = Number(form.bottomTimeMin);
    if (!site || !date) {
      setError("Dive site and date are required.");
      return;
    }
    if (!Number.isFinite(depth) || depth < 0) {
      setError("Max depth must be a number ≥ 0 (use 0 for pool / exhibit).");
      return;
    }
    if (!Number.isFinite(time) || time <= 0) {
      setError("Bottom time must be a positive number (minutes).");
      return;
    }

    const maxBytes = 900_000;
    const maxPhotos = 8;
    if (pendingFiles.length > maxPhotos) {
      setError(`At most ${maxPhotos} photos per dive.`);
      return;
    }
    for (const f of pendingFiles) {
      if (f.size > maxBytes) {
        setError(
          `"${f.name}" is too large (max ~${Math.round(maxBytes / 1000)} KB for this demo).`,
        );
        return;
      }
    }

    setSaving(true);
    try {
      const photos: string[] = [];
      for (const file of pendingFiles) {
        photos.push(await readFileAsDataUrl(file));
      }
      const body = {
        creatorSlug,
        diveSite: site,
        date,
        maxDepthM: depth,
        bottomTimeMin: time,
        waterTempC: form.waterTempC.trim()
          ? Number(form.waterTempC)
          : undefined,
        visibilityM: form.visibilityM.trim()
          ? Number(form.visibilityM)
          : undefined,
        gasMix: form.gasMix.trim() || undefined,
        notes: form.notes.trim() || undefined,
        photos,
      };
      const res = await fetch("/api/dive-logs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        setError(j.error ?? "Could not save dive.");
        return;
      }
      const created = (await res.json()) as DiveLogEntry;
      setLogs((prev) => [created, ...prev]);
      resetForm();
      setShowForm(false);
      setShowHistory(true);
    } catch {
      setError("Could not read one of the images or save the dive.");
    } finally {
      setSaving(false);
    }
  };

  const removeLog = async (id: string) => {
    try {
      const res = await fetch(`/api/dive-logs/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (res.status === 204) {
        setLogs((prev) => prev.filter((d) => d.id !== id));
      }
    } catch {
      /* ignore */
    }
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  return (
    <section className="mt-14 border-t border-white/10 pt-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-white">Dive log</h2>
          <p className="mt-2 max-w-2xl text-sm text-teal-100/75">
            {canManageDives
              ? `Add or remove dives for ${displayName}—the same log as your captain dashboard.`
              : `${displayName}'s public dive log. Sign in as this captain to add dives.`}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!loading && logs.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="rounded-xl border border-white/20 px-4 py-2.5 text-sm font-medium text-teal-100 transition hover:bg-white/10"
            >
              {showHistory
                ? "Hide history"
                : `Show history (${logs.length})`}
            </button>
          )}
          {canManageDives ? (
            <button
              type="button"
              onClick={() => {
                setShowForm(true);
                setError(null);
              }}
              className="rounded-xl bg-teal-400 px-4 py-2.5 text-sm font-semibold text-teal-950 transition hover:bg-teal-300"
            >
              Add dive
            </button>
          ) : !viewerSignedIn ? (
            <Link
              href={`/auth/signin?callbackUrl=/creators/${encodeURIComponent(creatorSlug)}`}
              className="rounded-xl border border-white/20 px-4 py-2.5 text-sm font-medium text-teal-100 transition hover:bg-white/10"
            >
              Sign in as captain to add dives
            </Link>
          ) : (
            <span className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-teal-200/70">
              Only this captain can add dives
            </span>
          )}
        </div>
      </div>

      {loadError && (
        <p className="mt-4 text-sm text-rose-300">{loadError}</p>
      )}

      {showForm && canManageDives && (
      <form
        onSubmit={handleSubmit}
        className="mt-6 grid gap-4 rounded-2xl border border-white/10 bg-black/20 p-4 sm:grid-cols-2 sm:p-6"
      >
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Dive site
          </label>
          <input
            required
            value={form.diveSite}
            onChange={(e) =>
              setForm((f) => ({ ...f, diveSite: e.target.value }))
            }
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none"
            placeholder="e.g. Blue Corner, Palau"
          />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Date
          </label>
          <input
            required
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white focus:border-teal-400/50 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Max depth (m)
          </label>
          <input
            required
            inputMode="decimal"
            value={form.maxDepthM}
            onChange={(e) =>
              setForm((f) => ({ ...f, maxDepthM: e.target.value }))
            }
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white focus:border-teal-400/50 focus:outline-none"
            placeholder="0 for surface / exhibit"
          />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Bottom time (min)
          </label>
          <input
            required
            inputMode="numeric"
            value={form.bottomTimeMin}
            onChange={(e) =>
              setForm((f) => ({ ...f, bottomTimeMin: e.target.value }))
            }
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white focus:border-teal-400/50 focus:outline-none"
            placeholder="e.g. 45"
          />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Water temp (°C)
          </label>
          <input
            inputMode="decimal"
            value={form.waterTempC}
            onChange={(e) =>
              setForm((f) => ({ ...f, waterTempC: e.target.value }))
            }
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white focus:border-teal-400/50 focus:outline-none"
            placeholder="Optional"
          />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Visibility (m)
          </label>
          <input
            inputMode="decimal"
            value={form.visibilityM}
            onChange={(e) =>
              setForm((f) => ({ ...f, visibilityM: e.target.value }))
            }
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white focus:border-teal-400/50 focus:outline-none"
            placeholder="Optional"
          />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Gas mix
          </label>
          <input
            value={form.gasMix}
            onChange={(e) => setForm((f) => ({ ...f, gasMix: e.target.value }))}
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white focus:border-teal-400/50 focus:outline-none"
            placeholder="e.g. Air, Nitrox 32%"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Notes
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none"
            placeholder="Conditions, buddies, highlights…"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
            Photos
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) =>
              setPendingFiles(Array.from(e.target.files ?? []))
            }
            className="mt-1 w-full text-sm text-teal-100/80 file:mr-3 file:rounded-lg file:border-0 file:bg-teal-400/20 file:px-3 file:py-2 file:text-teal-100"
          />
          <p className="mt-1 text-xs text-teal-200/60">
            Stored in SQLite as JSON (keep images small, &lt; ~900 KB each, up
            to 8). Use object storage for production.
          </p>
        </div>
        {error && (
          <p className="sm:col-span-2 text-sm text-rose-300">{error}</p>
        )}
        <div className="flex flex-wrap gap-3 sm:col-span-2">
          <button
            type="submit"
            disabled={saving || loading}
            className="rounded-xl bg-teal-400 px-5 py-2.5 font-semibold text-teal-950 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save dive"}
          </button>
          <button
            type="button"
            onClick={resetForm}
            className="rounded-xl border border-white/20 px-5 py-2.5 text-sm text-teal-100 hover:bg-white/5"
          >
            Clear form
          </button>
          <button
            type="button"
            onClick={closeForm}
            className="rounded-xl border border-white/20 px-5 py-2.5 text-sm text-teal-100 hover:bg-white/5"
          >
            Cancel
          </button>
        </div>
      </form>
      )}

      {showHistory && (
      <ul className="mt-6 space-y-6">
        {loading ? (
          <li className="text-sm text-teal-200/70">Loading dives…</li>
        ) : logs.length === 0 ? (
          <li className="text-sm text-teal-200/70">No dives logged yet.</li>
        ) : (
          logs.map((dive) => (
            <li
              key={dive.id}
              className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {dive.diveSite}
                  </h3>
                  <p className="mt-1 text-sm text-teal-200/80">
                    {dive.date} · {dive.maxDepthM} m · {dive.bottomTimeMin} min
                    {dive.waterTempC != null &&
                      ` · ${dive.waterTempC}°C`}
                    {dive.visibilityM != null &&
                      ` · viz ${dive.visibilityM} m`}
                    {dive.gasMix && ` · ${dive.gasMix}`}
                  </p>
                  {dive.notes && (
                    <p className="mt-3 text-sm leading-relaxed text-teal-100/85">
                      {dive.notes}
                    </p>
                  )}
                </div>
                {canManageDives && (
                  <button
                    type="button"
                    onClick={() => void removeLog(dive.id)}
                    className="shrink-0 self-start rounded-lg border border-white/15 px-3 py-1.5 text-xs text-teal-200 hover:bg-white/10"
                  >
                    Remove
                  </button>
                )}
              </div>
              {dive.photos.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {dive.photos.map((src, i) => (
                    <div
                      key={`${dive.id}-ph-${i}`}
                      className="relative h-24 w-32 overflow-hidden rounded-lg border border-white/10 bg-black/30 sm:h-28 sm:w-40"
                    >
                      {src.startsWith("data:") ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={src}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Image
                          src={src}
                          alt=""
                          fill
                          className="object-cover"
                          sizes="160px"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))
        )}
      </ul>
      )}

      {!loading &&
        !loadError &&
        logs.length === 0 &&
        !showForm &&
        canManageDives && (
          <p className="mt-4 text-sm text-teal-200/70">
            No dives yet—use <strong className="text-teal-100">Add dive</strong>{" "}
            to log one.
          </p>
        )}

      {!loading &&
        !loadError &&
        logs.length === 0 &&
        !canManageDives && (
          <p className="mt-4 text-sm text-teal-200/70">No dives logged yet.</p>
        )}
    </section>
  );
}
